import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { ArrowLeft, Search } from 'lucide-react';
import { useState } from 'react';
import DatePicker from 'react-datepicker';
import { Link } from 'react-router';
import type { SalesOrderDeliveryStatus, SalesOrderListStatusFilter } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { useSalesOrderList } from '@/api/hanly-v2/sales-orders/sales-orders';
import { formatYMD, isSupportedDate, parseSupportedDate } from '@/utils/date';

const statusTabItems: { key: SalesOrderListStatusFilter; label: string }[] = [
  { key: 'in_progress', label: '未完了' },
  { key: 'completed', label: '完了' },
];

const getBorderColor = (deliveryStatus?: SalesOrderDeliveryStatus): string => {
  if (deliveryStatus === 'INIT') return 'border-l-red-600';
  if (deliveryStatus === 'IN_PROGRESS') return 'border-l-amber-300';
  return 'border-l-green-600';
};

export const PickingPage = () => {
  const [statusTab, setStatusTab] = useState<SalesOrderListStatusFilter>('in_progress');
  const [todayOnly, setTodayOnly] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // 日付範囲は暦日で送り、暦日の解釈はサーバに任せる
  const shippingDateParams = (() => {
    if (todayOnly) {
      const today = format(new Date(), 'yyyy-MM-dd');
      return { from_shipping_date: today, to_shipping_date: today };
    }
    return {
      ...(fromDate ? { from_shipping_date: fromDate } : {}),
      ...(toDate ? { to_shipping_date: toDate } : {}),
    };
  })();

  const { data, isLoading } = useSalesOrderList({
    status_filter: statusTab,
    ordering: 'shipping_date',
    page_size: 100,
    ...shippingDateParams,
    ...(searchText ? { search: searchText } : {}),
  });

  const orders = data?.results ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="bg-white px-2 py-1">
        <Link to="/" className="flex items-center gap-2 font-bold">
          <ArrowLeft className="size-5" />
          ピッキング＆出荷
        </Link>
      </div>

      <div className="px-5 pb-3">
        {/* ステータスタブ */}
        <div className="flex border-b border-neutral-300 mb-3">
          {statusTabItems.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setStatusTab(tab.key)}
              className={`flex-1 py-1 text-center font-bold cursor-pointer ${
                statusTab === tab.key ? 'bg-sky-800 text-white' : 'text-zinc-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* フィルター */}
        <div className="flex items-center gap-3 mb-1">
          <div>
            <button
              type="button"
              onClick={() => {
                const next = !todayOnly;
                setTodayOnly(next);
                if (next) {
                  const today = format(new Date(), 'yyyy-MM-dd');
                  setFromDate(today);
                  setToDate(today);
                } else {
                  setFromDate('');
                  setToDate('');
                }
              }}
              className={`shrink-0 px-3 py-1 rounded-sm cursor-pointer ${
                todayOnly ? 'bg-gray-400 text-white' : 'bg-neutral-200 text-zinc-800'
              }`}
            >
              本日出荷
            </button>
          </div>
          <div className="flex-1 flex items-center gap-x-1">
            <DatePicker
              selected={parseSupportedDate(fromDate)}
              onChange={(date: Date | null) => {
                if (date && !isSupportedDate(date)) return;
                setFromDate(date ? format(date, 'yyyy-MM-dd') : '');
                setTodayOnly(false);
              }}
              dateFormat="yyyy/MM/dd"
              locale={ja}
              maxDate={parseSupportedDate(toDate) ?? undefined}
              placeholderText="開始日"
              isClearable
              className="w-full border border-neutral-400 rounded px-2 py-1 bg-white"
            />
            <span className="text-neutral-400">-</span>
            <DatePicker
              selected={parseSupportedDate(toDate)}
              onChange={(date: Date | null) => {
                if (date && !isSupportedDate(date)) return;
                setToDate(date ? format(date, 'yyyy-MM-dd') : '');
                setTodayOnly(false);
              }}
              dateFormat="yyyy/MM/dd"
              locale={ja}
              minDate={parseSupportedDate(fromDate) ?? undefined}
              placeholderText="終了日"
              isClearable
              className="w-full border border-neutral-400 rounded px-2 py-1 bg-white"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 border border-neutral-200 rounded px-2 py-1 bg-white">
          <Search className="size-5 text-neutral-400 shrink-0" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="得意先名、受注No."
            className="!border-0 !p-0 flex-1"
          />
        </div>

        {/* 受注一覧 */}
        <div className="flex flex-col gap-2 mt-3">
          {isLoading && <p className="text-sm text-neutral-500 text-center py-4">読み込み中...</p>}
          {!isLoading && orders.length === 0 && (
            <p className="text-sm text-neutral-500 text-center py-4">該当する受注はありません</p>
          )}
          {orders.map((order) => (
            <Link
              key={order.id}
              to={`/picking/${order.id}`}
              className={`bg-white border-l-3 border-solid px-3 py-2 flex flex-col gap-1 ${getBorderColor(order.delivery_status)}`}
            >
              <div className="flex flex-col gap-0.5">
                <p className="text-xs leading-relaxed">受注No.{order.id}</p>
                <p className="text-base leading-7">{order.supplier_partner_customer_name}</p>
              </div>
              <div className="flex items-center">
                <span className="text-sm text-neutral-500 leading-relaxed w-20 mt-0.5">出荷予定日:</span>
                <span className="font-bold text-zinc-800">{formatYMD(order.shipping_date)}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};
